import {chain} from 'lodash';
import {connect} from 'react-redux';

import {CollapsedItemStates, computeIsItemExpanded} from '../../../../helpers/expansionTogglingHelpers';
import {makeMapStateToProps} from '../../../../helpers/reselectHelpers';
import {InboxModel} from '../../../../models/api/inboxModel';
import {CollapsedItemKeyPrefixesEnum} from '../../../../models/api/localPreferencesModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {VisualWorkspaceTeamData} from '../../../../models/api/visualWorkspaceModel';
import {
  InterfaceEventsEnum,
  trackInterfaceEvent,
  WorkspaceDisplaySectionTrackingTypesEnum,
} from '../../../../services/tracking';
import {getInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {WorkspaceDropReorderTeamInboxHandler} from '../../../../store/data/workspace/reorder/types';
import {TeamInboxModelMap} from '../../../../store/data/workspace/visualWorkspace/visualWorkspaceHelpers';
import {canDropWorkspaceItemAtTarget} from '../dnd/workspaceDragDropHelpers';
import {WorkspaceDragSourceTypesEnum, WorkspaceItemDropHandler} from '../dnd/workspaceDragDropTypes';
import {ToggleExpandItemHandler} from './context/types';
import {TeamWorkspaceEditorInboxes} from './teamWorkspaceEditorInboxes';

/*
 * Constants.
 */

const defaultIsTeamInboxesExpanded = true;

/*
 * Props.
 */

interface TeamWorkspaceEditorInboxesContainerProps {
  team: TeamModel;
  teamData: VisualWorkspaceTeamData;
  /** Hash map of workspace item expansion toggle states. */
  collapsedItems: CollapsedItemStates;
  /** Handler for toggling the expanded/collapsed state of section items. */
  onToggleExpandSection: ToggleExpandItemHandler;
  teamInboxModelMap: TeamInboxModelMap;
  onDropReorderTeamInbox: WorkspaceDropReorderTeamInboxHandler;
}

export interface SelectableInbox {
  isSelected: boolean;
  inbox: InboxModel;
  isRecentlyViewed: boolean | undefined;
}

/*
 * Selectors.
 */

function buildTeamInboxesExpandedKey(teamId: number) {
  return `${CollapsedItemKeyPrefixesEnum.TEAM_INBOXES}tim:${teamId}`;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<TeamWorkspaceEditorInboxesContainerProps>()(() => ({
  allInboxes: getInboxes,
}));

/**
 * This component is adapted from <TeamWorkspaceInboxesContainer /> but for use in <WorkspacesEditor />.
 * @see TeamWorkspaceInboxesContainer
 */
export const TeamWorkspaceEditorInboxesContainer = connect(
  mapStateToProps,
  null,
  (stateProps, dispatchProps, ownProps) => {
    const {allInboxes} = stateProps;
    const {teamData, collapsedItems, onToggleExpandSection, teamInboxModelMap, onDropReorderTeamInbox} =
      ownProps;

    const onWorkspaceItemDrop: WorkspaceItemDropHandler = (droppedItem, linePosition, target) => {
      if (!target || !canDropWorkspaceItemAtTarget(droppedItem, target)) {
        return;
      }

      if (
        droppedItem.type !== WorkspaceDragSourceTypesEnum.TEAM_INBOX ||
        target.type !== WorkspaceDragSourceTypesEnum.TEAM_INBOX
      ) {
        return;
      }

      trackInterfaceEvent(InterfaceEventsEnum.P1_EDIT_REORDER, {
        section: WorkspaceDisplaySectionTrackingTypesEnum.TEAM_INBOX,
        if_auto_suggested: false,
      });
      onDropReorderTeamInbox({
        teamId: ownProps.team.id,
        droppedItemId: droppedItem.id,
        targetItemId: target.id,
        linePosition,
      });
    };

    // Build the method to toggle the expanded state.
    const workspaceKey = buildTeamInboxesExpandedKey(teamData.teamId);
    const isExpanded = computeIsItemExpanded(collapsedItems, workspaceKey, defaultIsTeamInboxesExpanded);
    const onToggleExpandClick = () =>
      onToggleExpandSection({key: workspaceKey, defaultExpandedState: defaultIsTeamInboxesExpanded});

    const inboxes = chain(teamData.inboxes)
      .map((visualWorkspaceInbox) => {
        // Find the InboxModel from either the inboxes in Redux state or from the TeamInboxModelMap.
        const inbox = allInboxes[visualWorkspaceInbox.id] ?? teamInboxModelMap[visualWorkspaceInbox.id];
        return inbox
          ? {
              inbox,
              isSelected: visualWorkspaceInbox.isSelected,
              isRecentlyViewed: visualWorkspaceInbox.isRecentlyViewed,
            }
          : null;
      })
      .compact()
      .value();

    // Merge the props.
    return {
      ...stateProps,
      ...ownProps,
      onToggleExpandClick,
      onWorkspaceItemDrop,
      isExpanded,
      inboxes,
    };
  },
)(TeamWorkspaceEditorInboxes);
